/**
 * Seller Instruction Portal Page
 * ISOLATED MODULE - Uses /api/sales-instructions/* endpoints only
 * Route: /portal/seller/instruction
 * 
 * Seller can:
 * - View instruction summary (read-only)
 * - View and sign contract
 * - View and approve/request changes to brochure
 * - Upload documents (ID, leasehold docs)
 * 
 * Seller CANNOT:
 * - Edit pricing
 * - Generate brochures
 * - Publish to portals
 * - Access other properties
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Home, MapPin, Bed, Bath, FileText, FolderOpen, BookOpen,
  Loader2, CheckCircle, Clock, AlertCircle, XCircle,
  Upload, Eye, Download, Check, X, MessageSquare,
  Building2, User, Shield
} from "lucide-react";
import { API } from "@/App";

// Status colors
const contractStatusColors = {
  NOT_SENT: "bg-slate-100 text-slate-600",
  SENT: "bg-amber-100 text-amber-600",
  VIEWED: "bg-blue-100 text-blue-600",
  SIGNED: "bg-green-100 text-green-600",
  DECLINED: "bg-red-100 text-red-600"
};

const brochureStatusColors = {
  DRAFT: "bg-slate-100 text-slate-600",
  GENERATED: "bg-blue-100 text-blue-600",
  SENT_TO_SELLER: "bg-amber-100 text-amber-600",
  CHANGES_REQUESTED: "bg-orange-100 text-orange-600",
  APPROVED: "bg-green-100 text-green-600",
  OVERRIDE_APPROVED: "bg-green-100 text-green-600"
};

const SellerInstructionPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("portal_token") || localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  
  // Data states
  const [instruction, setInstruction] = useState(null);
  const [contract, setContract] = useState(null);
  const [brochure, setBrochure] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [party, setParty] = useState(null);

  // Upload refs
  const documentInputRef = useRef(null);

  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRequestChangesDialog, setShowRequestChangesDialog] = useState(false);
  const [changeNotes, setChangeNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [documentCategory, setDocumentCategory] = useState("SELLER_ID");

  useEffect(() => {
    fetchSellerInstruction();
  }, []);

  const fetchSellerInstruction = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/sales-instructions/seller/my-instruction`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      setInstruction(data.instruction);
      setContract(data.contract);
      setBrochure(data.brochure);
      setDocuments(data.documents || []);
      setParty(data.party);
    } catch (error) {
      console.error("Error fetching seller instruction:", error);
      if (error.response?.status === 404) {
        toast.error("No instruction assigned to your account");
      } else {
        toast.error("Failed to load your instruction");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", documentCategory);
      formData.append("title", file.name);

      await axios.post(
        `${API}/sales-instructions/seller/documents`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Document uploaded successfully");
      fetchSellerInstruction();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleApproveBrochure = async () => {
    try {
      await axios.post(
        `${API}/sales-instructions/brochure/${brochure?.id}/seller-approve`,
        { notes: "Approved by seller" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Brochure approved");
      setShowApproveDialog(false);
      fetchSellerInstruction();
    } catch (error) {
      toast.error("Failed to approve brochure");
    }
  };

  const handleRequestChanges = async () => {
    if (!changeNotes.trim()) {
      toast.error("Please describe the changes you need");
      return;
    }

    try {
      await axios.post(
        `${API}/sales-instructions/brochure/${brochure?.id}/seller-request-changes`,
        { notes: changeNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Change request submitted");
      setShowRequestChangesDialog(false);
      setChangeNotes("");
      fetchSellerInstruction();
    } catch (error) {
      toast.error("Failed to submit change request");
    }
  };

  const tabs = [
    { id: "summary", label: "Summary", icon: Home },
    { id: "contract", label: "Contract", icon: FileText },
    { id: "brochure", label: "Brochure", icon: BookOpen },
    { id: "documents", label: "Documents", icon: FolderOpen }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading your instruction...</p>
        </div>
      </div>
    );
  }

  if (!instruction) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Instruction Found</h2>
            <p className="text-slate-500 mb-4">
              You don't have any property instruction assigned to your account yet.
              Please contact your agent if you believe this is an error.
            </p>
            <Button variant="outline" onClick={() => navigate("/portal/seller")}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Shield className="w-4 h-4" />
                Seller Portal
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                {instruction.address_line_1}
              </h1>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {instruction.town}, {instruction.postcode}
              </p>
            </div>
            <div className="text-right">
              <Badge className="bg-blue-100 text-blue-700">
                {instruction.status?.replace(/_/g, " ")}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Summary Tab */}
        {activeTab === "summary" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Property Details
                </CardTitle>
                <CardDescription>
                  Read-only summary of your property instruction
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-slate-500">Address</Label>
                    <p className="font-medium">{instruction.address_line_1}</p>
                    {instruction.address_line_2 && (
                      <p className="text-sm text-slate-600">{instruction.address_line_2}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-500">Location</Label>
                    <p className="font-medium">{instruction.town}</p>
                    <p className="text-sm text-slate-600">{instruction.postcode}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Property Type</Label>
                    <p className="font-medium">{instruction.property_type || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Bedrooms</Label>
                    <p className="font-medium flex items-center gap-1">
                      <Bed className="w-4 h-4 text-slate-400" />
                      {instruction.bedrooms || "—"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Bathrooms</Label>
                    <p className="font-medium flex items-center gap-1">
                      <Bath className="w-4 h-4 text-slate-400" />
                      {instruction.bathrooms || "—"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Tenure</Label>
                    <p className="font-medium">{instruction.tenure || "—"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Status Summary */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      contract?.status === "SIGNED" ? "bg-green-100" : "bg-amber-100"
                    }`}>
                      <FileText className={`w-5 h-5 ${
                        contract?.status === "SIGNED" ? "text-green-600" : "text-amber-600"
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Contract</p>
                      <Badge className={contractStatusColors[contract?.status] || "bg-slate-100"}>
                        {contract?.status || "Not Started"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      brochure?.status?.includes("APPROVED") ? "bg-green-100" : "bg-blue-100"
                    }`}>
                      <BookOpen className={`w-5 h-5 ${
                        brochure?.status?.includes("APPROVED") ? "text-green-600" : "text-blue-600"
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Brochure</p>
                      <Badge className={brochureStatusColors[brochure?.status] || "bg-slate-100"}>
                        {brochure?.status?.replace(/_/g, " ") || "Not Generated"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Seller Info */}
            {party && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Your Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500">Name</Label>
                      <p className="font-medium">{party.name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Email</Label>
                      <p className="font-medium">{party.email}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Contract Tab */}
        {activeTab === "contract" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Instruction Contract</CardTitle>
                <CardDescription>
                  View and sign your instruction contract
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-6">
                  <Badge className={`${contractStatusColors[contract?.status]} text-lg px-4 py-2`}>
                    {contract?.status || "NOT_SENT"}
                  </Badge>
                  {contract?.sent_at && (
                    <span className="text-sm text-slate-500">
                      Sent: {new Date(contract.sent_at).toLocaleDateString()}
                    </span>
                  )}
                  {contract?.signed_at && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Signed: {new Date(contract.signed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {contract?.status === "NOT_SENT" && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-600">
                      Your agent has not yet sent the instruction contract. 
                      You will be notified when it's ready for signing.
                    </p>
                  </div>
                )}

                {contract?.status === "SENT" && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Contract Awaiting Signature</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Please review and sign the contract. Contact your agent if you have any questions.
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          Note: In the current version, please sign the contract physically and your agent 
                          will upload the signed copy.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {contract?.status === "SIGNED" && (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">Contract Signed</p>
                          <p className="text-sm text-green-700">
                            Your instruction contract has been signed and is now active.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Show signed contract document */}
                    {documents.filter(d => d.category === "CONTRACT_SIGNED").map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <FileText className="w-5 h-5 text-green-600" />
                        <div className="flex-1">
                          <div className="font-medium">{doc.title}</div>
                          <div className="text-sm text-slate-500">
                            Signed on {new Date(doc.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={`${API.replace("/api", "")}${doc.file_url}`} target="_blank" rel="noopener">
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Brochure Tab */}
        {activeTab === "brochure" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Property Brochure</CardTitle>
                <CardDescription>
                  Review and approve your property brochure before it goes live
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-6">
                  <Badge className={`${brochureStatusColors[brochure?.status] || "bg-slate-100"} text-lg px-4 py-2`}>
                    {brochure?.status?.replace(/_/g, " ") || "Not Generated"}
                  </Badge>
                </div>

                {!brochure && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-600">
                      Your agent has not yet generated the property brochure. 
                      You will be notified when it's ready for review.
                    </p>
                  </div>
                )}

                {brochure?.status === "GENERATED" && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-700">
                      The brochure has been generated and is being reviewed by your agent.
                      You will be notified when it's ready for your approval.
                    </p>
                  </div>
                )}

                {brochure?.status === "SENT_TO_SELLER" && (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Approval Required</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Please review the brochure and either approve it or request changes.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button variant="outline" asChild>
                        <a href={`${API.replace("/api", "")}${brochure.pdf_file_url}`} target="_blank" rel="noopener">
                          <Eye className="w-4 h-4 mr-2" />
                          Preview Brochure
                        </a>
                      </Button>
                      <Button onClick={() => setShowApproveDialog(true)} className="bg-green-600 hover:bg-green-700">
                        <Check className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button variant="outline" onClick={() => setShowRequestChangesDialog(true)}>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Request Changes
                      </Button>
                    </div>
                  </div>
                )}

                {brochure?.status === "CHANGES_REQUESTED" && (
                  <div className="space-y-4">
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-orange-800">Changes Requested</p>
                          <p className="text-sm text-orange-700 mt-1">
                            You've requested changes to the brochure. Your agent will update it and send a new version.
                          </p>
                          {brochure.seller_notes && (
                            <div className="mt-3 p-3 bg-white rounded border">
                              <p className="text-xs text-slate-500 mb-1">Your notes:</p>
                              <p className="text-sm">{brochure.seller_notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(brochure?.status === "APPROVED" || brochure?.status === "OVERRIDE_APPROVED") && (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">Brochure Approved</p>
                          <p className="text-sm text-green-700">
                            The brochure has been approved and may be used for marketing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button variant="outline" asChild>
                      <a href={`${API.replace("/api", "")}${brochure.pdf_file_url}`} target="_blank" rel="noopener">
                        <Download className="w-4 h-4 mr-2" />
                        Download Brochure
                      </a>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Documents</CardTitle>
                  <CardDescription>
                    Upload required documents such as ID, lease documents, and management information
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {/* Upload Section */}
                <div className="p-4 bg-slate-50 rounded-lg mb-6">
                  <h3 className="font-medium mb-3">Upload New Document</h3>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label className="text-sm text-slate-600 mb-1 block">Document Type</Label>
                      <Select value={documentCategory} onValueChange={setDocumentCategory}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SELLER_ID">Seller ID (Passport/Driving License)</SelectItem>
                          <SelectItem value="LEASE">Lease Document</SelectItem>
                          <SelectItem value="MANAGEMENT_INFO">Management Company Info</SelectItem>
                          <SelectItem value="SERVICE_CHARGE">Service Charge Statement</SelectItem>
                          <SelectItem value="GROUND_RENT">Ground Rent Information</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <input
                        type="file"
                        ref={documentInputRef}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={(e) => {
                          if (e.target.files[0]) handleDocumentUpload(e.target.files[0]);
                        }}
                      />
                      <Button onClick={() => documentInputRef.current?.click()} disabled={uploading}>
                        {uploading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Documents List */}
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No documents uploaded yet</p>
                    <p className="text-sm">Upload your ID and any relevant property documents above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                        <FileText className="w-5 h-5 text-slate-500" />
                        <div className="flex-1">
                          <div className="font-medium">{doc.title}</div>
                          <div className="text-xs text-slate-500">
                            {doc.category.replace(/_/g, " ")} • Uploaded {new Date(doc.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {doc.uploaded_by_role}
                        </Badge>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={`${API.replace("/api", "")}${doc.file_url}`} target="_blank" rel="noopener">
                            <Eye className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Required Documents Checklist */}
                <div className="mt-6 p-4 border rounded-lg">
                  <h3 className="font-medium mb-3">Required Documents Checklist</h3>
                  <div className="space-y-2">
                    {[
                      { category: "SELLER_ID", label: "Photo ID (Passport or Driving License)" },
                      { category: "SELLER_ID", label: "Proof of Address (Utility Bill)" },
                      { category: "LEASE", label: "Lease Document (if leasehold)", optional: true },
                      { category: "MANAGEMENT_INFO", label: "Management Company Details (if leasehold)", optional: true }
                    ].map((item, idx) => {
                      const hasDoc = documents.some(d => d.category === item.category);
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {hasDoc ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                          )}
                          <span className={hasDoc ? "text-green-700" : "text-slate-600"}>
                            {item.label}
                          </span>
                          {item.optional && (
                            <Badge variant="outline" className="text-xs">Optional</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Approve Brochure Dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Brochure</AlertDialogTitle>
            <AlertDialogDescription>
              By approving this brochure, you confirm that all information is accurate and you're happy 
              for it to be used in marketing your property.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApproveBrochure} className="bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-2" />
              Approve Brochure
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Request Changes Dialog */}
      <AlertDialog open={showRequestChangesDialog} onOpenChange={setShowRequestChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Please describe the changes you'd like made to the brochure.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="e.g., Please update the number of bedrooms to 4, and change the description of the kitchen..."
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              rows={4}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setChangeNotes("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRequestChanges}>
              Submit Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SellerInstructionPage;
